import React, { useRef, useEffect, useState, useCallback } from 'react';
import Hls from 'hls.js'; // Import hls.js
import { Loader } from './Index';
import { useAppSelector } from '../store/hooks';

const VideoPlayer: React.FC = () => {
    const [isSticky, setIsSticky] = useState(false);
    const darkMode = useAppSelector((state: any) => state.categoryNavigation.darkmode);
    const staticData = useAppSelector((state: any) => state.staticSettingSlice.data);  

    const videoRef = useRef<HTMLVideoElement>(null);
    const hlsRef = useRef<Hls | null>(null); // Ref to store the hls.js instance
    const videoWrapperRef = useRef<HTMLDivElement>(null);
    const [videoHeight, setVideoHeight] = useState<number>(0);

    const [currentSource, setCurrentSource] = useState<'pre' | 'live' | 'fallback'>('pre');
    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [needsUserInteraction, setNeedsUserInteraction] = useState(false);

    const [preaddStatus, setPreaddStatus] = useState(staticData?.pre_add_status || false);
    const [preAdUrl, setPreAdUrl] = useState(staticData?.pre_add_url || '');
    const [livetvUrl, setLivetvUrl] = useState(staticData?.liveTv_url || '');
const [forceStickyFalse, setForceStickyFalse] = useState(false);

// console.log(staticData.liveTv_url, " staticData.liveTv_url") //https://cdn.ottlive.co.in/bharat24/index.fmp4.m3u8
useEffect(() => {
  let observer: ResizeObserver | null = null;

  const checkForElement = setInterval(() => {
    const element = videoWrapperRef.current;
    if (element) {
      observer = new ResizeObserver((entries) => {
        for (let entry of entries) {      
          setVideoHeight(entry.contentRect.height);
        }
      });

      observer.observe(element);
      clearInterval(checkForElement); // stop checking once ref is found
    }    
  }, 100);

  return () => {
    clearInterval(checkForElement);
    if (observer) observer.disconnect(); // cleanup ResizeObserver too
  };
   
}, []);


useEffect(() => {
  const container = document.querySelector('.home-container'); // use actual class/ID
  const handleScroll = () => {
    const scrollY = container?.scrollTop || 0;
if(scrollY > 400){
    setIsSticky(true);
} else {
    setIsSticky(false);
//  setForceStickyFalse(false)
}
  };

  container?.addEventListener('scroll', handleScroll);
  return () => container?.removeEventListener('scroll', handleScroll);
}, []);

    useEffect(() => {
        if (staticData && Object.keys(staticData).length > 0) {
            setPreaddStatus(staticData.pre_add_status);
            setPreAdUrl(staticData.pre_add_url);
            setLivetvUrl(staticData.liveTv_url);
        }
    }, [staticData]);

    const setupVideo = useCallback(async (src: string, type: 'mp4' | 'hls') => {
        setIsLoading(true);
        setErrorMessage(null);
        // Do NOT reset needsUserInteraction here. It should persist if already true,
        // so we don't keep trying to autoplay.

        const videoElement = videoRef.current;
        if (!videoElement) {
            setErrorMessage("Video element not available.");
            setIsLoading(false);
            return;
        }

        // Dispose of any existing hls.js instance
        if (hlsRef.current) {
            hlsRef.current.destroy();
            hlsRef.current = null;
        }

        videoElement.src = '';
        videoElement.load();
        videoElement.muted = false; // Ensure video is not muted by default

        // --- KEY FIX: Prevent repeated play attempts if user interaction is already needed ---
        if (needsUserInteraction) {         
            setIsLoading(false); // Stop loading animation as we are waiting for user input
            return; // Do not attempt to play again
        }

        const tryPlay = async () => {
       try {
  await videoElement.play();
  setIsLoading(false);
  setNeedsUserInteraction(false);
  setErrorMessage(null);
} catch (e: any) {
  if ((e.name === "NotAllowedError" || e.name === "AbortError") && !videoElement.muted) {
    // 🔁 Try muted autoplay
    console.warn("Autoplay with sound failed, retrying with muted video...");
    videoElement.muted = true;
    try {
      await videoElement.play();
      setIsLoading(false);
      setNeedsUserInteraction(false);
      setErrorMessage(null);
    } catch (err) {
      console.warn("Muted autoplay also failed. Waiting for user interaction.");
      setErrorMessage("Click to play the video");
      setNeedsUserInteraction(true);
      setIsLoading(false);
    }
  } else {
    console.error(`Video error: ${e.message}`);
    setErrorMessage(`Video error: ${e.message}`);
    setNeedsUserInteraction(true);
    setIsLoading(false);
  }
}

        };

        if (type === 'hls' && Hls.isSupported()) {
            hlsRef.current = new Hls();
            hlsRef.current.loadSource(src);
            hlsRef.current.attachMedia(videoElement);

            hlsRef.current.on(Hls.Events.MANIFEST_PARSED, () => {               
                // Only try to play if we haven't already hit the NotAllowedError condition
                if (!needsUserInteraction) { // Double check here too
                    tryPlay();
                }
            });

            hlsRef.current.on(Hls.Events.ERROR, (event, data) => {
                console.error("HLS.js Error:", data.type, data.details, data.fatal ? '(fatal)' : '');
                if (data.fatal) {
                    setErrorMessage(`Fatal HLS error: ${data.details}. Try refreshing.`);
                    setIsLoading(false);
                    setNeedsUserInteraction(true); // User might need to retry
                    if (hlsRef.current) hlsRef.current.destroy();
                } else {
                    setErrorMessage(`HLS error: ${data.details}. Attempting to recover...`);
                }
            });

        } else if (type === 'mp4' || videoElement.canPlayType('application/vnd.apple.mpegurl')) {
            videoElement.src = src;
            videoElement.load();

            const handleMp4ReadyToPlay = () => {              
                // Only try to play if not already waiting for user
                if (!needsUserInteraction) { // Double check here too
                    tryPlay();
                } else {
                    setIsLoading(false); // Already waiting for user
                }
                videoElement.removeEventListener('canplay', handleMp4ReadyToPlay); // Use canplay
            };
            videoElement.addEventListener('canplay', handleMp4ReadyToPlay, { once: true });

            // Fallback for cases where 'canplay' might not fire or autoplay is just slow
            const timeoutId = setTimeout(() => {
                // Only attempt play if paused, no current error message, and not already waiting for interaction
                if (videoElement.paused && !errorMessage && !needsUserInteraction) {
                    console.warn(`Timeout reached, 'canplay' not fired for ${type}. Attempting to play anyway.`);
                    tryPlay(); // Try playing as a fallback
                }
            }, 3000);

            videoElement.addEventListener('canplay', () => clearTimeout(timeoutId), { once: true });

        } else {
            setErrorMessage("Your browser does not support playing this video type.");
            setIsLoading(false);
        }
    }, [needsUserInteraction]); // Keep needsUserInteraction as a dependency, but the guard prevents loop

    const handlePreVideoEnded = useCallback(() => {
        setCurrentSource('live');
        const videoElement = videoRef.current;
        if (videoElement) {
            videoElement.pause(); // Pause pre-roll before starting live
        }
        setupVideo(livetvUrl, 'hls');
    }, [setupVideo, livetvUrl]);

    // Main useEffect for initializing video playback
    useEffect(() => {
        if (!staticData || Object.keys(staticData).length === 0 || !livetvUrl) {
            setIsLoading(true);
            setErrorMessage(null);
            setNeedsUserInteraction(false); // Reset on initial load if data is missing
            return;
        }




        const videoElement = videoRef.current;
        if (!videoElement) return;

        // Clean up previous event listeners before setting up new ones
        videoElement.removeEventListener('ended', handlePreVideoEnded);
        // Ensure error listener is properly managed to prevent multiple attachments
        // Use a named function for handleError if it's dynamic
        // For simplicity, we'll ensure it's removed before adding.
        const prevHandleError = (e: Event) => { /* no-op, just for removal */ }; // Placeholder for removal
        videoElement.removeEventListener('error', prevHandleError); // Remove old one if it exists

        // --- Only initialize video if not already in a "waiting for user" state ---
        // This prevents re-initializing the video and triggering the autoplay block repeatedly
        if (!needsUserInteraction) {
            if (preaddStatus && preAdUrl) {
                setCurrentSource('pre');
                setupVideo(preAdUrl, 'mp4');
                videoElement.addEventListener('ended', handlePreVideoEnded);
            } else if (livetvUrl) {
                setCurrentSource('live');
                setupVideo(livetvUrl, 'hls');
            } else {
                setErrorMessage("No valid video source found from static data.");
                setIsLoading(false);
                setNeedsUserInteraction(true); // If no valid source, user can't play, show error
                return;
            }
        } else {
            // If already in needsUserInteraction state, just ensure loading is false
            setIsLoading(false);
            return;
        }


        const handleError = (e: Event) => {
            console.error("Error playing current video (native event):", e);
            setErrorMessage("Video failed to load/play. Please try again.");
            setIsLoading(false);
            setNeedsUserInteraction(true); // User interaction may be required to recover
        };
        videoElement.addEventListener('error', handleError);


        return () => {
            // Cleanup on unmount or effect re-run
            if (videoElement) {
                videoElement.removeEventListener('ended', handlePreVideoEnded);
                videoElement.removeEventListener('error', handleError);
            }
            if (hlsRef.current) {
                hlsRef.current.destroy();
                hlsRef.current = null;
            }
        };
    }, [preaddStatus, preAdUrl, livetvUrl, setupVideo, handlePreVideoEnded, staticData, needsUserInteraction]); // Added needsUserInteraction here too for explicit logic

    // Handle retry button click
    const handleRetry = () => {
        setErrorMessage(null);
        setNeedsUserInteraction(false); // Reset interaction flag to allow re-attempt
        // Re-trigger the main useEffect logic by calling setupVideo based on current state
        if (preaddStatus && preAdUrl) {
            setCurrentSource('pre');
            setupVideo(preAdUrl, 'mp4');
        } else if (livetvUrl) {
            setCurrentSource('live');
            setupVideo(livetvUrl, 'hls');
        } else {
            setErrorMessage("Cannot start video: Missing essential configuration.");
            setIsLoading(false);
            setNeedsUserInteraction(true);
        }
    };


    if (!staticData || Object.keys(staticData).length === 0 || !livetvUrl || (preaddStatus && !preAdUrl)) {
        return <Loader darkMode={darkMode} />;
    }

    const containerStyle: React.CSSProperties = {
        width: '100%',
        maxWidth: '800px',
        margin: '0px auto',
        backgroundColor: '#000',
        minHeight: '200px',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
    };

    const videoElementStyle: React.CSSProperties = {
        width: '100%',
        height: 'auto',
        maxHeight: '450px',
        display: isLoading || errorMessage ? 'none' : 'block' // Hide video if loading or error
    };

    const overlayStyle: React.CSSProperties = {
        position: 'relative',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        color: '#fff',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.2em',
        padding: '20px',
        textAlign: 'center',
        zIndex: 0,
    };

    // Define the CSS as a string
    const liveStreamCss = `
        .live-video-player::-webkit-media-controls-timeline,
        .live-video-player::-webkit-media-controls-current-time-display,
        .live-video-player::-webkit-media-controls-time-remaining-display {
            display: none !important;
        }

        /* Firefox specific */
        .live-video-player::-moz-range-track,
        .live-video-player::-moz-range-thumb {
            display: none !important;
        }
        .live-video-player::-moz-range-progress {
            display: none !important;
        }
    `;

const closeSticky = () => { 
    setIsSticky(false);
    setForceStickyFalse(true)
}
    return (
        <div>
                    {isSticky && (
  <div  style={{ height: `${videoHeight}px`,top:'100px',background:"red",width:'300px' }} > </div> // Adjust to match video height
)}
{/* forceStickyFalse */}
        <div ref={videoWrapperRef} className={`video-wrapper ${isSticky && !forceStickyFalse ? 'sticky-video' : ''}`} style={containerStyle}>

            <div className={`video-container relative`}>

                 {isSticky && !forceStickyFalse && (<button onClick={closeSticky} className='closeSticky absolute w-12 h-12 rounded-full bg-red-800 -top-20 right-0 z-10 scale-150 border-1 border-red-700 opacity-90 text-xl p-0'>x</button>)}

            <video
                ref={videoRef}
                controls
                playsInline
                preload="auto"
                poster={staticData?.liveTv_thumb || ''}
                style={videoElementStyle}
                className={currentSource === 'live' ? 'live-video-player' : ''}
            />
                </div>
            
  
            {/* Loading Overlay */}
            {isLoading && !errorMessage && (
                <div style={overlayStyle}>
                    <div>
                        {currentSource === 'pre' && preaddStatus ? 'Loading pre-roll...' :
                            currentSource === 'live' ? 'Connecting to live stream...' :
                                'Initializing video player...'}
                    </div>
                    <div style={{ border: '4px solid rgba(255,255,255,0.3)', borderTop: '4px solid #fff', borderRadius: '50%', width: '30px', height: '30px', animation: 'spin 1s linear infinite' }}></div>
                    <style>{`
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg);}
                        }
                    `}</style>
                </div>
            )}

            {/* Error Overlay (now includes user interaction prompt) */}
           {errorMessage && (
                <div style={overlayStyle}>
                    {/* <p>{errorMessage}</p> */}
                    {needsUserInteraction && (
                        <button
                            onClick={() => {
                                handleRetry();
                            }}
                            style={{
                                marginTop: '15px',
                                padding: '10px 20px',
                                background: '#28a745', // Green for Play
                                color: '#fff',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '1em',
                            }}
                        >
                            Click to Go Live...
                        </button>
                    )}
                    {!needsUserInteraction && (
                        <button
                            onClick={handleRetry}
                            style={{
                                marginTop: '15px',
                                padding: '10px 20px',
                                background: '#dc3545', // Red for Retry
                                color: '#fff',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '1em',
                            }}
                        >
                            Retry Video
                        </button>
                    )}
                </div>
            )}

            {/* Add the CSS for hiding time and progress for live streams using dangerouslySetInnerHTML */}
            {currentSource === 'live' && (
                <style dangerouslySetInnerHTML={{ __html: liveStreamCss }} />
            )}
        </div>
        </div>
    );
};

export default VideoPlayer;